import { countBy, groupBy, sortBy, uniq } from "lodash";
import { SthDate } from "../parsers/sthDatesParser";
import { Ptc } from "../parsers/ptcParser";
import {
  BLANK_STH_TAXABILITY,
  CT_THRESHOLD,
  NEW_PTC_NAME,
  NONE_THRESHOLD,
  PTC_NAME_SEPARATOR,
  STH_TAXABILITY_SEPARATOR,
} from "../utils/constants";
import { Atin } from "../parsers/sthAtinParser";
import { isPositiveFiniteNumber, isStringPositiveNumber } from "../utils/numberUtils";

interface AtinGroupPtcCount {
  baselineTaxability: string;
  secondaryTaxability: string;
  ptc: string;
  count: number;
}

export interface ConfigChange {
  ptc: Ptc;
  secondaryTaxability: string;
  atinsMapped: Atin[];
}

// Baseline taxability to STH taxability to ATINs
type AtinGroup = Record<string, Record<string, Atin[]>>;

export const getNewAtinToPtcMappingsForSth = (
  atins: Map<string, Atin>,
  ptcs: Map<string, Ptc>,
  excludedPtcNames?: string[]
) => {
  const atinsGroupedByTaxability = groupAffectedAtinsByTaxability(Array.from(atins.values()));

  const { ptcResourcePool, changedAtinToPtcMappings } = determinePtcsForSth(
    Array.from(ptcs.values()),
    atinsGroupedByTaxability,
    excludedPtcNames
  );

  const baselineToPtcCount = getBaselineToPtcCount(atinsGroupedByTaxability);

  const allConfigChangesForAffected = generateNewPtcMappingForAffectedAtins(
    ptcResourcePool,
    ptcs,
    baselineToPtcCount,
    atinsGroupedByTaxability
  );

  const overallAtinToPtcMappingChanges = { ...changedAtinToPtcMappings };

  allConfigChangesForAffected.forEach((configChange) => {
    configChange.atinsMapped.forEach((atin) => {
      overallAtinToPtcMappingChanges[atin.atinId] = configChange.ptc;
    });
  });

  return overallAtinToPtcMappingChanges;
};

export const filterQualifyingSTHs = (
  inputStartDate: Date,
  inputEndDate: Date,
  sthDates: SthDate[]
): Map<string, string[]> => {
  const affectedSthToJurisdictions: Map<string, string[]> = new Map();
  for (const sthDate of sthDates) {
    if (areDateRangesOverlapping(inputStartDate, inputEndDate, sthDate.stateDate, sthDate.endDate)) {
      if (!affectedSthToJurisdictions.has(sthDate.sthType)) {
        affectedSthToJurisdictions.set(sthDate.sthType, []);
      }
      affectedSthToJurisdictions.get(sthDate.sthType).push(sthDate.jurisdiction);
    }
  }

  for (const [sthType, jurisdictions] of affectedSthToJurisdictions) {
    affectedSthToJurisdictions.set(sthType, uniq(jurisdictions).sort());
  }
  return affectedSthToJurisdictions;
};

const groupAffectedAtinsByTaxability = (atins: Atin[]) => {
  const affectedATINs = atins.filter(isAtinAffected);
  const groupedAtins: AtinGroup = {};
  const atinsGroupedByBaseline = groupBy(affectedATINs, (atin) => atin.mappedPtc.baselineTaxability);

  for (const baseline in atinsGroupedByBaseline) {
    groupedAtins[baseline] = groupBy(atinsGroupedByBaseline[baseline], (atin) => atin.secondaryTaxability);
  }

  return groupedAtins;
};

const getBaselineToPtcCount = (atinGroups: AtinGroup) => {
  const baselineToPtcCount: Record<string, AtinGroupPtcCount[]> = {};
  for (const baseline in atinGroups) {
    for (const secondary in atinGroups[baseline]) {
      const atins = atinGroups[baseline][secondary];
      const ptcCount = countBy(atins, (atin) => atin.mappedPtc.ptcName);
      for (const ptc in ptcCount) {
        if (!(baseline in baselineToPtcCount)) {
          baselineToPtcCount[baseline] = [];
        }
        baselineToPtcCount[baseline].push({
          baselineTaxability: baseline,
          secondaryTaxability: secondary,
          ptc: ptc,
          count: ptcCount[ptc],
        });
      }
    }
    if (baseline in baselineToPtcCount) {
      baselineToPtcCount[baseline].sort((count1, count2) => count2.count - count1.count);
    }
  }
  return baselineToPtcCount;
};

const generateNewPtcMappingForAffectedAtins = (
  ptcResourcePool: Record<string, Ptc[]>,
  ptcNameToPtcMap: Map<string, Ptc>,
  baselineToPtcCount: Record<string, AtinGroupPtcCount[]>,
  groupedAtins: AtinGroup
) => {
  let newPtcCount = 0;
  const ptcUpdates: ConfigChange[] = [];

  for (const baseline in groupedAtins) {
    const potentialPtcs = new Set((ptcResourcePool[baseline] ?? []).map((ptc) => ptc.ptcName));
    const secondaryTaxabilitiesAssigned = new Set<string>();

    for (const ptcCount of baselineToPtcCount[baseline]) {
      if (potentialPtcs.size === 0) {
        break;
      }
      if (!potentialPtcs.has(ptcCount.ptc) || secondaryTaxabilitiesAssigned.has(ptcCount.secondaryTaxability)) {
        continue;
      }
      const atins = groupedAtins[baseline][ptcCount.secondaryTaxability];
      const existingPtcUpdate: ConfigChange = {
        ptc: ptcNameToPtcMap.get(ptcCount.ptc),
        secondaryTaxability: ptcCount.secondaryTaxability,
        atinsMapped: atins,
      };
      ptcUpdates.push(existingPtcUpdate);
      secondaryTaxabilitiesAssigned.add(ptcCount.secondaryTaxability);
      potentialPtcs.delete(ptcCount.ptc);
    }

    const ptcsLeftOver = Array.from(potentialPtcs);
    for (const secondary in groupedAtins[baseline]) {
      if (secondaryTaxabilitiesAssigned.has(secondary)) {
        continue;
      }
      const atins = groupedAtins[baseline][secondary];

      if (ptcsLeftOver.length > 0) {
        const ptcToUse = ptcsLeftOver[0];
        const existingPtcUpdate: ConfigChange = {
          ptc: ptcNameToPtcMap.get(ptcToUse),
          secondaryTaxability: secondary,
          atinsMapped: atins,
        };
        ptcUpdates.push(existingPtcUpdate);
        ptcsLeftOver.shift();
      } else {
        const newPtc: Ptc = {
          ptcName: createNewPtcName(newPtcCount + 1),
          baselineTaxability: baseline,
          isNew: true,
          atinsMapped: atins,
        };

        newPtcCount += 1;
        const newPtcUpdate: ConfigChange = {
          ptc: newPtc,
          secondaryTaxability: secondary,
          atinsMapped: atins,
        };
        ptcUpdates.push(newPtcUpdate);
      }
    }
  }

  return ptcUpdates;
};

const determinePtcsForSth = (
  ptcs: Ptc[],
  atinGroup: AtinGroup,
  excludedPtcs?: string[]
): { ptcResourcePool: Record<string, Ptc[]>; changedAtinToPtcMappings: Record<string, Ptc> } => {
  const ptcsForSTH: Record<string, Ptc[]> = {}; // map from baseline taxability pattern to PTCs
  let ptcsToCheck: Ptc[] = [];
  const unaffectedPtcs = new Set<Ptc>();
  const changedAtinToPtcMappings: Record<string, Ptc> = {};
  const excludedPtcsSet = new Set(excludedPtcs ?? []);

  ptcs.forEach((ptc) => {
    if (excludedPtcsSet.has(ptc.ptcName)) {
      return;
    }

    if (isPtcCompletedAffected(ptc)) {
      (ptcsForSTH[ptc.baselineTaxability] || (ptcsForSTH[ptc.baselineTaxability] = [])).push(ptc);
    } else if (doesPtcHaveAffectedAtins(ptc)) {
      ptcsToCheck.push(ptc);
    } else {
      unaffectedPtcs.add(ptc);
    }
  });

  // Check PTCs that have least number of ATINs mapped first
  ptcsToCheck = sortBy(ptcsToCheck, (ptc) => ptc.atinsMapped.length);

  // Check if can remap unaffected ATINs
  ptcsToCheck.forEach((ptc) => {
    // Check for each baseline taxability pattern, we do not need more PTCs than number of unique STH taxability patterns
    const numPTCsForBaseline = ptcsForSTH[ptc.baselineTaxability]?.length ?? 0;
    const numSthTaxabiltyPatterns = Object.keys(atinGroup[ptc.baselineTaxability] ?? {}).length;
    if (numPTCsForBaseline >= numSthTaxabiltyPatterns) {
      return;
    }
    const ptcsWithSameBaselineTaxability = findPtcsWithBaselineTaxability(ptc.baselineTaxability, ptcs).filter(
      (ptcSameBaselineTaxability) => unaffectedPtcs.has(ptcSameBaselineTaxability) && ptc !== ptcSameBaselineTaxability
    );
    if (ptcsWithSameBaselineTaxability.length) {
      (ptcsForSTH[ptc.baselineTaxability] || (ptcsForSTH[ptc.baselineTaxability] = [])).push(ptc);
      // Remap unaffected ATINs
      ptc.atinsMapped
        .filter((atin) => !isAtinAffected(atin))
        .forEach((atin) => (changedAtinToPtcMappings[atin.atinId] = ptcsWithSameBaselineTaxability[0]));
    }
  });

  return { ptcResourcePool: ptcsForSTH, changedAtinToPtcMappings };
};

export const isPtcValidAlternateOptionForSth = (ptc: Ptc) => {
  return !ptc.isUsedForSth && isPtcCompletedAffected(ptc);
};

export const isPtcCompletedAffected = (ptc: Ptc) => {
  return ptc.atinsMapped.every(isAtinAffectedOrDeprecated);
};

export const isAtinAffectedOrDeprecated = (atin: Atin) => {
  return isAtinAffected(atin) || atin.deprecated;
};

const doesPtcHaveAffectedAtins = (ptc: Ptc) => {
  return ptc.atinsMapped.some(isAtinAffected);
};

export const findPtcsWithBaselineTaxability = (baselineTaxability: string, allPtcs: Ptc[]) => {
  return allPtcs.filter((ptc) => ptc.baselineTaxability === baselineTaxability);
};

const areDateRangesOverlapping = (startA: Date, endA: Date, startB: Date, endB: Date) => {
  return startA <= endB && endA >= startB;
};

export const isAtinAffected = (atin: Atin) => {
  return Array.from(atin.secondaryTaxability).some(
    (char) => char !== BLANK_STH_TAXABILITY && char !== STH_TAXABILITY_SEPARATOR
  );
};

export const getNewPtcName = (allPtcs: Map<string, Ptc>) => {
  const newPtcDigitSuffixes = Array.from(allPtcs.values())
    .filter((ptc) => ptc.ptcName.startsWith(NEW_PTC_NAME))
    .map((ptc) => {
      const digitSuffix = ptc.ptcName.match(/\d+$/);
      if (digitSuffix.length) {
        return parseInt(digitSuffix[0]);
      } else {
        return 0;
      }
    });
  const newPtcDigit = newPtcDigitSuffixes.length ? Math.max(...newPtcDigitSuffixes) + 1 : 1;
  return createNewPtcName(newPtcDigit);
};

const createNewPtcName = (num: number) => `${NEW_PTC_NAME}${PTC_NAME_SEPARATOR}${num}`;

export const isValidSthTaxability = (sthTaxability: string) => {
  const sthTaxabilityTrimmed = sthTaxability.trim();
  return (
    sthTaxabilityTrimmed.length === 0 ||
    sthTaxabilityTrimmed.toLowerCase() === NONE_THRESHOLD.toLowerCase() ||
    isStringPositiveNumber(sthTaxabilityTrimmed) ||
    isStringValidCumulativeThreshold(sthTaxabilityTrimmed)
  );
};

const isStringValidCumulativeThreshold = (cumulativeThreshold: string) => {
  const numberOfThreshold = parseFloat(cumulativeThreshold);
  return (
    isPositiveFiniteNumber(numberOfThreshold) &&
    `${numberOfThreshold}${CT_THRESHOLD}`.toLowerCase() === cumulativeThreshold.toLowerCase()
  );
};
